/**
 * TodayHeroCard — Calm, single-purpose To-Do List hero.
 *
 * Layout:
 *   - Optional small "Due today / Overdue · {goal}" chip above the card
 *   - One soft tinted card with a single primary action: + Add to-do
 *   - Whole card is tappable (same action as the button)
 */
import { useMemo } from 'react';
import { ChevronRight, Clock, AlertTriangle, Plus, Target } from 'lucide-react';
import { startOfToday, isBefore, isSameDay, parseISO, isValid, format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Goal } from '@/types/todo';
import { isBucketItem } from '@/lib/bucketPlan';


interface TodayHeroCardProps {
  goals: Goal[];
  onAddGoal: () => void;
  onAddTodo: () => void;
  onLogToday: () => void;
}

function pickUrgentGoal(goals: Goal[]): { kind: 'overdue' | 'today'; goal: Goal } | null {
  const today = startOfToday();
  // Bucket List experiences are never "due" — the BucketPlanSpotlight handles them.
  const active = goals.filter(g => !g.completedAt && (g.progress ?? 0) < 100 && !g.isExample && !isBucketItem(g));
  const overdue = active
    .filter(g => g.dueDate && isValid(parseISO(g.dueDate)) && isBefore(parseISO(g.dueDate), today))
    .sort((a, b) => parseISO(a.dueDate!).getTime() - parseISO(b.dueDate!).getTime())[0];
  if (overdue) return { kind: 'overdue', goal: overdue };
  const dueToday = active.find(
    g => g.dueDate && isValid(parseISO(g.dueDate)) && isSameDay(parseISO(g.dueDate), today),
  );
  if (dueToday) return { kind: 'today', goal: dueToday };
  return null;
}

export function TodayHeroCard({ goals, onAddGoal, onAddTodo, onLogToday }: TodayHeroCardProps) {
  const urgent = useMemo(() => pickUrgentGoal(goals), [goals]);



  const dateLabel = format(new Date(), 'EEEE, d MMMM');
  // Editorial metadata stack — derived only from goals already in memory.
  const stats = useMemo(() => {
    const real = goals.filter(g => !g.isExample);
    const done = real.filter(g => g.completedAt || (g.progress ?? 0) >= 100).length;
    const bucket = real.filter(g => isBucketItem(g) && !g.completedAt).length;
    return { inFlight: real.length - done - bucket, bucket, done };
  }, [goals]);

  return (
    <section
      aria-label="Today's briefing"
      className="gd-surface relative overflow-hidden rounded-[32px]"
    >
      {/* Ambient golden-hour wash */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary-glow/25 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.10),transparent_70%)]" />
      <div className="relative grid gap-5 p-5 sm:p-7 lg:p-9 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center md:gap-8">
        <div aria-hidden className="hidden md:flex flex-col items-center justify-center pr-8 border-r border-border/60">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary/80">{format(new Date(), 'MMM')}</span>
          <span className="gd-numeral text-6xl lg:text-7xl font-bold text-foreground">{format(new Date(), 'd')}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{format(new Date(), 'EEEE')}</span>
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary/80">Today's briefing · {dateLabel}</p>
          {urgent ? (
            <button
              onClick={onLogToday}
              className="group mt-3 block w-full text-left transition-transform duration-150 ease-out active:scale-[0.98]"
              style={{ touchAction: 'manipulation' }}
              aria-label={`${urgent.kind === 'overdue' ? 'Overdue' : 'Due today'}: ${urgent.goal.title}`}
            >
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
                urgent.kind === 'overdue' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
              }`}>
                {urgent.kind === 'overdue' ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                {urgent.kind === 'overdue' ? 'Overdue' : 'Due today'}
              </span>
              <span className="mt-2 flex items-center gap-2 gd-headline text-2xl sm:text-3xl lg:text-4xl text-foreground">
                <span className="line-clamp-2">{urgent.goal.title}</span>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          ) : (
            <>
              <h2 className="mt-3 gd-headline text-2xl sm:text-3xl lg:text-4xl text-foreground">
                A clear day ahead.
              </h2>
              <p className="mt-1.5 max-w-prose text-sm sm:text-base leading-relaxed text-muted-foreground">
                Nothing is pressing. Jot down one small thing you want to get done today.
              </p>
            </>
          )}
          {stats.inFlight + stats.bucket + stats.done > 0 && (<>
          <div className="mt-5 gd-hairline" aria-hidden />
          <dl className="mt-4 grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: 'In flight', v: stats.inFlight },
              { k: 'Bucket list', v: stats.bucket },
              { k: 'Lived', v: stats.done },
            ].map((m) => (
              <div key={m.k}>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{m.k}</dt>
                <dd className="gd-numeral mt-1 text-2xl sm:text-3xl font-bold text-foreground">{m.v}</dd>
              </div>
            ))}
          </dl>
          </>)}
        </div>
        <div className="flex flex-wrap gap-2 md:flex-col md:items-stretch">
          <Button onClick={onAddTodo} className="gap-1.5 rounded-full transition-transform active:scale-[0.98]">
            <Plus className="h-4 w-4" /> Add to-do
          </Button>
          <Button onClick={onAddGoal} variant="outline" className="gap-1.5 rounded-full transition-transform active:scale-[0.98]">
            <Target className="h-4 w-4" /> New goal
          </Button>
        </div>
      </div>
    </section>
  );
}
